import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import emailjs from '@emailjs/browser';

const FORM_FIELDS = [
  { type: 'select', label: 'I am a:', name: 'role', options: ['Artist', 'Record Label', 'Manager', 'Distributor'] },
  { type: 'url', label: 'Spotify link to the release you are submitting', name: 'spotifyLink', placeholder: 'https://open.spotify.com/...' },
  { type: 'text', label: 'Current Distributor', name: 'distributor', placeholder: 'Enter distributor name' },
  { type: 'select', label: 'Current Spotify Monthly Listeners', name: 'listeners', options: ['Under 100,000', '250,000', '500,000', '1M+'] },
  // { type: 'select', label: 'What are your goals for this campaign?', name: 'goals', options: ['Increase Streams', 'Increase Monthly Listeners', 'Increase Followers'] },
  // { type: 'select', label: 'What level of streaming growth are you targeting?', name: 'growth', options: ['< 50,000', '100,000', '250,000', '500,000', '1M'] },
  { type: 'select', label: 'Are you the primary decision maker for this release?', name: 'approval', options: ['Yes', 'No'] }
];

const CustomSelect = ({ name, value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
  };

  return (
    <div className={`custom-select ${isOpen ? 'open' : ''}`} ref={wrapperRef}>
      <div className="select-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span style={{ opacity: value ? 1 : 0.5 }}>{value || placeholder}</span>
      </div>
      <div className="select-options">
        {options.map((opt, i) => (
          <div key={i} className={`select-option ${value === opt ? 'selected' : ''}`} onClick={() => handleSelect(opt)}>
            {opt}
          </div>
        ))}
      </div>
    </div>
  );
};

const DemoForm = ({ isDemoFormHidden, handleShowDemoForm }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', workEmail: '', number: '', role: '', spotifyLink: '', distributor: '', listeners: '', approval: '' });

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const closeForm = () => {
    handleShowDemoForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    // 1. Verify that all Custom Select dropdowns have a value selected
    if (!formData.role || !formData.listeners || !formData.approval) {
      setErrorMessage('Please make a selection for all dropdown questions before continuing.');
      return;
    }

    // 2. Validate the Spotify Link
    const spotifyRegex = /^(https?:\/\/)?(open\.spotify\.com|spoti\.fi|spotify\.link)\//i;
    if (!spotifyRegex.test(formData.spotifyLink)) {
      setErrorMessage('Please enter a valid Spotify URL (e.g., https://open.spotify.com/...).');
      return;
    }

    // Check if user is NOT the primary decision maker
    if (formData.approval === 'No') {
      setFormData({ name: '', workEmail: '', number: '', role: '', spotifyLink: '', distributor: '', listeners: '', approval: '' });
      setCurrentStep('thank-you-rejected');
      return;
    }

    setIsSubmitting(true);
    try {
      await emailjs.send(
        'service_vnw6uhv',
        'template_e3viedl',
        formData,
        'UzpTzmdCV-DZ6y-Kl'
      );

      setFormData({ name: '', workEmail: '', number: '', role: '', spotifyLink: '', distributor: '', listeners: '', approval: '' });
      setCurrentStep('thank-you-success');
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('There was an error sending your request. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`demo-form-wrapper ${isDemoFormHidden ? 'hidden' : ''}`}>
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <button className="close-btn" onClick={closeForm} aria-label="Close form">×</button>

            {currentStep === 'thank-you-rejected' && (
              <div className="thank-you-screen text-center mx-auto">
                <h2>Thank you for your request</h2>
                <p>Thank you for your interest in INR - We're glad you reached out.</p>
                <p>At this time, we're focused on supporting a <em>specific</em> set of releases, so, based on your responses, we may not be the right fit just yet.</p>
                <p>We truly appreciate your interest and hope you'll consider checking back on future releases.</p>
                <div className="thank-you-footer">
                  <h3>Have a specific question?</h3>
                  <p>Feel free to email us at <a href="mailto:norequests@itsnorequests.com">norequests@itsnorequests.com</a></p>
                </div>
              </div>
            )}

            {currentStep === 'thank-you-success' && (
              <div className="thank-you-screen text-center mx-auto">
                <h2>Submission received</h2>
                <p>We'll review and be in touch.</p>
              </div>
            )}

            {(currentStep === 1 || currentStep === 2) && (
              <form onSubmit={currentStep === 2 ? handleSubmit : (e) => { e.preventDefault(); setCurrentStep(2); }}>
                {currentStep === 1 && (
                  <div className="form-step">
                    <div className="mb-5">
                      <h2>Book a 30-minute call*</h2>
                      <p>Find out if your music is a good fit for INR</p>
                    </div>
                    <div className="form-group mb-4">
                      <label htmlFor="name">Name *</label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group mb-4">
                      <label htmlFor="workEmail">Work Email *</label>
                      <input type="email" id="workEmail" name="workEmail" value={formData.workEmail} onChange={handleInputChange} required />
                    </div>
                    <div className="form-group mb-4">
                      <label htmlFor="number">Number *</label>
                      <input type="tel" id="number" name="number" value={formData.number} onChange={handleInputChange} required />
                    </div>
                    <div className="contact-info mb-4 text-center">
                      <p>Questions? email our team at <a href="mailto:norequests@itsnorequests.com">norequests@itsnorequests.com</a></p>
                    </div>
                    <button type="submit" className="btn-submit w-100">OK</button>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="form-step">
                    {FORM_FIELDS.map((field, idx) => (
                      <div className="form-group mb-4" key={idx}>
                        <label htmlFor={field.name}>{field.label}</label>
                        {field.type === 'select' ? (
                          <CustomSelect name={field.name} value={formData[field.name]} onChange={handleInputChange} placeholder="Select an option" options={field.options} />
                        ) : (
                          <input type={field.type} id={field.name} name={field.name} placeholder={field.placeholder} value={formData[field.name]} onChange={handleInputChange} required />
                        )}
                      </div>
                    ))}

                    {errorMessage && (
                      <div className="error-message text-center mb-3" style={{ fontWeight: 'bold' }}>
                        {errorMessage}
                      </div>
                    )}

                    <div className="d-flex gap-3 mt-5">
                      <button type="button" className="btn-submit btn-back" onClick={() => setCurrentStep(1)} disabled={isSubmitting}>Back</button>
                      <button type="submit" className="btn-submit flex-grow-1" disabled={isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send Request'}
                      </button>
                    </div>
                  </div>
                )}

              </form>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DemoForm;